<template>
  <div class="odontogram-item">
    <st-data-display label="Anotações gerais" v-if="generalNotes">
      <div class="text-pre" v-html="generalNotes" />
    </st-data-display>

    <st-data-display label="Odontograma" v-if="hasFilledTeeth">
      <div class="teeth-summary">
        <div v-for="tooth in filledTeeth" :key="tooth.number" class="tooth-card">
          <div class="tooth-header">
            <span class="tooth-number">Dente {{ tooth.number }}</span>
            <span class="tooth-condition" :class="`condition-${tooth.condition}`">
              {{ conditionLabel(tooth.condition) }}
            </span>
          </div>
          <div class="tooth-details">
            <div v-if="tooth.surfaces && tooth.surfaces.length > 0" class="tooth-surfaces">
              <strong>Faces:</strong>
              <div class="surfaces-list">
                <span
                  v-for="(surface, index) in getSurfacesArray(tooth.surfaces)"
                  :key="index"
                  class="surface-badge"
                >
                  {{ surface }}
                </span>
              </div>
            </div>
            <div v-if="tooth.notes" class="tooth-notes">
              <strong>Observações:</strong> {{ tooth.notes }}
            </div>
          </div>
        </div>
      </div>
    </st-data-display>
  </div>
</template>

<script>
import { transformApiDataToOdontograma } from '../helpers/odontogramaHelpers';

export default {
  props: {
    item: {
      type: Object,
    },
  },
  computed: {
    generalNotes() {
      if (this.item && this.item.anamnesis && this.item.anamnesis.inputs) {
        const contentInput = this.item.anamnesis.inputs.content;
        return contentInput || '';
      }
      return '';
    },
    teeth() {
      return transformApiDataToOdontograma(this.item.anamnesis);
    },
    filledTeeth() {
      return this.teeth.filter(
        tooth => tooth.condition || tooth.surfaces.length > 0 || tooth.notes,
      );
    },
    hasFilledTeeth() {
      return this.filledTeeth.length > 0;
    },
  },
  methods: {
    conditionLabel(condition) {
      const labels = {
        healthy: 'Sadio',
        carious: 'Cariado',
        restored: 'Restaurado',
        missing: 'Ausente',
        crown: 'Coroa',
        canal: 'Canal',
      };
      return labels[condition] || condition;
    },
    getSurfacesArray(surfaces) {
      if (Array.isArray(surfaces)) {
        return surfaces;
      }
      if (typeof surfaces === 'string') {
        return surfaces.split(',').map(s => s.trim()).filter(s => s);
      }
      return [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.odontogram-item {
  .teeth-summary {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: $layout-spacing;

    @media (max-width: $size-md) {
      grid-template-columns: 1fr;
    }
  }

  .tooth-card {
    background: $bg-color-light;
    border: 1px solid $border-color-dark;
    border-radius: 6px;
    padding: $layout-spacing-sm;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .tooth-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: $layout-spacing-sm;
      padding-bottom: $layout-spacing-sm;
      border-bottom: 1px solid $border-color;

      .tooth-number {
        font-weight: 700;
        color: $dark-color;
        font-size: 14px;
      }

      .tooth-condition {
        font-size: 12px;
        font-weight: 600;
        padding: 2px 8px;
        border-radius: 4px;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &.condition-healthy {
          background: lighten($success-color, 45%);
          color: $success-color;
        }

        &.condition-carious {
          background: lighten($error-color, 40%);
          color: $error-color;
        }

        &.condition-restored {
          background: lighten($primary-color, 45%);
          color: $primary-color;
        }

        &.condition-missing {
          background: lighten($gray-color-dark, 40%);
          color: $gray-color-dark;
        }

        &.condition-crown {
          background: lighten($warning-color, 40%);
          color: darken($warning-color, 10%);
        }

        &.condition-canal {
          background: lighten(#9c27b0, 45%);
          color: #9c27b0;
        }
      }
    }

    .tooth-details {
      font-size: 13px;
      color: $body-font-color;

      .tooth-surfaces {
        margin-bottom: $layout-spacing-sm;

        strong {
          color: $dark-color;
          display: block;
          margin-bottom: $layout-spacing-sm / 2;
        }

        .surfaces-list {
          display: flex;
          flex-wrap: wrap;
          gap: $layout-spacing-sm / 2;

          .surface-badge {
            display: inline-block;
            padding: 2px 8px;
            background: lighten($primary-color, 45%);
            color: darken($primary-color, 10%);
            border: 1px solid lighten($primary-color, 35%);
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.3px;
          }
        }
      }

      .tooth-notes {
        margin-bottom: $layout-spacing-sm;

        &:last-child {
          margin-bottom: 0;
        }

        strong {
          color: $dark-color;
          margin-right: 4px;
        }
      }
    }
  }
}
</style>
